import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  Snackbar,
  SnackbarContent,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import Header from "src/components/Header";
import NewClientModal from "src/components/NewClientModal";
import SearchForm from "src/components/SearchForm";
import DataTable from "src/components/Table";
import {
  deleteCustomer,
  getAllCustomersAndEquipments,
  getCustomer,
} from "src/services/customers";
import { StyledErrorContainer, StyledLoadingContainer } from "./styles";
import ErrorMessage from "src/components/ErrorMessage";

interface EquipmentRow {
  customerId: string;
  document: string;
  cliente: string;
  equipamento: string;
  numSerie: string;
  marca: string;
  tecnico: string;
  vencimento: string;
  vencimentoOriginal: string;
}

export const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [allCustomers, setAllCustomers] = useState<any[]>([]);
  const [tableRows, setTableRows] = useState<EquipmentRow[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [openModal, setOpenModal] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 20;

  const tableHeaders = [
    "Cliente",
    "Cpf/Cnpj",
    "Equipamento",
    "Nº série",
    "Marca",
    "Técnico",
    "Vencimento",
  ];

  const getEquipments = async () => {
    try {
      setLoading(true);
      const response = await getAllCustomersAndEquipments();
      const customers = response?.data?.data || [];
      setAllCustomers(customers);
      const rows = mapCustomersToRows(customers);
      setTableRows(rows);
      setCurrentPage(1);
    } catch {
      setToast({
        message: "Erro ao carregar lista de clientes.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const mapCustomersToRows = (customers: any[]): EquipmentRow[] => {
    const formatDate = (dateString: string) => {
      if (!dateString) return "-";
      const onlyDate = dateString.split("T")[0];
      const [year, month, day] = onlyDate.split("-");
      return `${day}/${month}/${year}`;
    };

    const rows = customers.flatMap((customer: any) =>
      (customer.equipment || []).map((eq: any) => ({
        customerId: customer.id,
        document: customer.document,
        cliente: customer.name,
        equipamento: eq.name,
        numSerie: eq.serial_number,
        marca: eq.brand,
        tecnico: eq.technician,
        vencimentoOriginal: eq.expiration_date,
        vencimento: formatDate(eq.expiration_date),
      }))
    );

    return rows.sort((a, b) => {
      const dateA = new Date(a.vencimentoOriginal).getTime();
      const dateB = new Date(b.vencimentoOriginal).getTime();
      return dateA - dateB;
    });
  };

  useEffect(() => {
    getEquipments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseModal = () => {
    setOpenModal(false);
    getEquipments();
  };

  const handleView = async (customerId: string) => {
    try {
      setLoading(true);
      const response = await getCustomer(customerId);
      setSelectedCustomer(response.data.data);
      setOpenModal(true);
    } catch {
      setToast({
        message: "Erro ao carregar dados do cliente.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (customerId: string) => {
    if (!customerId) return;

    const now = new Date();
    const day = now.getDate();
    const month = now.getMonth() + 1;
    const minute = now.getMinutes();

    const minuteStr = String(minute).padStart(2, "0");
    const key = `${minuteStr}${day * month}`;

    const input = prompt(
      `⚙️ Para confirmar a exclusão, digite a chave de acesso:`
    );

    if (String(input) !== String(key)) {
      alert("❌ Chave incorreta! Exclusão não permitida.");
      return;
    }

    if (
      !window.confirm(
        "Tem certeza que deseja excluir este cliente e seus equipamentos?"
      )
    )
      return;

    try {
      setLoading(true);
      const response = await deleteCustomer(customerId);
      if (response?.status === 204) {
        setToast({
          message: "Cliente excluído com sucesso! ✅",
          type: "success",
        });
        getEquipments();
      } else {
        setToast({ message: "Erro ao excluir cliente.", type: "error" });
      }
    } catch {
      setToast({ message: "Erro ao excluir cliente.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterResult = (filteredCustomers: any[]) => {
    const rows = mapCustomersToRows(filteredCustomers);
    setTableRows(rows);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(tableRows.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedRows = tableRows.slice(startIndex, startIndex + rowsPerPage);

  return (
    <div>
      {loading && (
        <StyledLoadingContainer>
          <CircularProgress />
        </StyledLoadingContainer>
      )}

      <Header />
      <SearchForm customers={allCustomers} onFilter={handleFilterResult} />

      {paginatedRows.length > 0 ? (
        <>
          <DataTable
            headers={tableHeaders}
            rows={paginatedRows}
            minWidthTable={800}
            onView={handleView}
            onDelete={handleDelete}
          />
          {totalPages > 1 && (
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={2}
              sx={{ mt: 2, mb: 4 }}
            >
              <Button
                variant="outlined"
                size="small"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              >
                Anterior
              </Button>
              <Typography>
                Página {currentPage} de {totalPages}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Próximo
              </Button>
            </Stack>
          )}
        </>
      ) : (
        !loading && (
          <StyledErrorContainer>

            <ErrorMessage message="Nenhum registro encontrado." type="warning" />
          </StyledErrorContainer>
        )
      )}
      {openModal && (
        <NewClientModal
          open={openModal}
          handleClose={handleCloseModal}
          customer={selectedCustomer}
        />
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <SnackbarContent
          message={toast?.message}
          sx={{
            backgroundColor: "#fff",
            color: toast?.type === "success" ? "#4caf50" : "#f44336",
            fontWeight: 700,
            borderRadius: 1,
          }}
        />
      </Snackbar>
    </div>
  );
};
